/**
 * Парсинг/запись meta-событий SMF (FF <type> <len> <data>).
 *
 * Известные фиксированные meta-типы берутся из `REQUIRED_LENGTHS`;
 * если декларированная VLQ-длина не совпадает — данные дополняются
 * нулями или обрезаются (толерантность к «диким» файлам вида
 * `FF 21 00`, `FF 51 00`).
 */

import type {
  MidiKeySignature,
  MidiMetaEvent,
  MidiTempo,
  MidiTimeSignature,
} from "../types.js";
import { KEY_SIGNATURE_MAP, KEY_SIGNATURE_KEYS } from "../types.js";
import { readVLQ, writeVLQ } from "./vlq.js";

/** Длины meta-полезной нагрузки, которые мы знаем заранее. */
export const REQUIRED_LENGTHS: Record<number, number> = {
  0x00: 2, // sequenceNumber
  0x20: 1, // channelPrefix
  0x21: 1, // portPrefix
  0x2f: 0, // endOfTrack
  0x51: 3, // setTempo
  0x54: 5, // smpteOffset
  0x58: 4, // timeSignature
  0x59: 2, // keySignature
};

/** Meta-типы с фиксированной/специальной обработкой, не пишутся в unknownMeta. */
export const UNKNOWN_META_TYPES = new Set([
  0x00, // sequenceNumber
  0x20, // channelPrefix
  0x21, // portPrefix
  0x2f, // endOfTrack
  0x51, // setTempo
  0x54, // smpteOffset
  0x58, // timeSignature
  0x59, // keySignature
]);

export const TEXT_META_TYPES = new Set([
  0x01, // text
  0x02, // copyright
  0x03, // trackName
  0x04, // instrumentName
  0x05, // lyric
  0x06, // marker
  0x07, // cue
]);

export const metaTypeName = (t: number): string => {
  switch (t) {
    case 0x01:
      return "text";
    case 0x02:
      return "copyright";
    case 0x03:
      return "trackName";
    case 0x04:
      return "instrumentName";
    case 0x05:
      return "lyrics";
    case 0x06:
      return "marker";
    case 0x07:
      return "cue";
    default:
      return `meta-${t.toString(16)}`;
  }
};

export const textTypeToByte = (type: string): number | null => {
  switch (type) {
    case "text":
      return 0x01;
    case "copyright":
      return 0x02;
    case "trackName":
      return 0x03;
    case "instrumentName":
      return 0x04;
    case "lyrics":
      return 0x05;
    case "marker":
      return 0x06;
    case "cue":
      return 0x07;
    default:
      return null;
  }
};

/** UTF-8 decode совместимого качества без зависимостей. */
export const decodeUtf8 = (bytes: Uint8Array, start: number, length: number): string => {
  let s = "";
  for (let i = 0; i < length; i += 1) {
    s += String.fromCharCode(bytes[start + i]!);
  }
  try {
    return decodeURIComponent(escape(s));
  } catch {
    return s;
  }
};

export const encodeUtf8 = (text: string): number[] => {
  const bytes: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code < 0x80) {
      bytes.push(code);
    } else if (code < 0x800) {
      bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else {
      bytes.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    }
  }
  return bytes;
};

/**
 * Результат парсинга meta-события. `dataStart`/`dataEnd` — индексы
 * байтов payload в исходном `Uint8Array`. Парсер дальше решает, что
 * с ними делать (собирает текст, извлекает tempo и т.п.).
 */
export type ParsedMeta = {
  metaType: number;
  dataStart: number;
  dataEnd: number;
  declaredEnd: number;
};

export const parseMetaEvent = (
  body: Uint8Array,
  pos: number,
  end: number
): { meta: ParsedMeta; next: number } | null => {
  if (pos >= end) return null;
  const metaType = body[pos]!;
  const lenVLQ = readVLQ(body, pos + 1, end);
  if (!lenVLQ) return null;
  const declared = lenVLQ.value;
  const dataStart = lenVLQ.next;
  const required = REQUIRED_LENGTHS[metaType];
  const actualLen = required !== undefined ? required : declared;
  const available = Math.min(actualLen, end - dataStart);
  const dataEnd = dataStart + available;
  const declaredEnd = dataStart + Math.min(declared, end - dataStart);
  return {
    meta: { metaType, dataStart, dataEnd, declaredEnd },
    next: declaredEnd,
  };
};

export const microsecondsToBpm = (us: number): number => 60_000_000 / us;

export const parseTempoEvent = (ticks: number, data: Uint8Array): MidiTempo | null => {
  if (data.length < 3) return null;
  const us = ((data[0]! << 16) | (data[1]! << 8) | data[2]!) >>> 0;
  if (us === 0) return null;
  return { ticks, bpm: microsecondsToBpm(us) };
};

export const parseTimeSignatureEvent = (ticks: number, data: Uint8Array): MidiTimeSignature | null => {
  if (data.length < 4) return null;
  return {
    ticks,
    timeSignature: [data[0]!, 1 << data[1]!],
  };
};

export const parseKeySignatureEvent = (ticks: number, data: Uint8Array): MidiKeySignature | null => {
  if (data.length < 2) return null;
  const signed = data[0]! > 127 ? data[0]! - 256 : data[0]!;
  const scale = data[1] === 0 ? "major" : "minor";
  const entry = KEY_SIGNATURE_MAP.find((k) => k.signed === signed);
  return {
    ticks,
    key: entry ? entry[scale] : (KEY_SIGNATURE_KEYS[Math.max(0, Math.min(14, signed + 7))] ?? "C"),
    scale,
  };
};

export const encodeKeySignatureBytes = (ks: MidiKeySignature): number[] => {
  const entry = KEY_SIGNATURE_MAP.find(
    (k) => (ks.scale === "major" ? k.major === ks.key : k.minor === ks.key)
  );
  const signed = entry ? entry.signed : 0;
  return [signed & 0xff, ks.scale === "major" ? 0 : 1];
};

export const encodeTempoBytes = (tempo: MidiTempo): number[] => {
  const us = Math.max(1, Math.round(60_000_000 / tempo.bpm));
  return [(us >> 16) & 0xff, (us >> 8) & 0xff, us & 0xff];
};

export const encodeTimeSignatureBytes = (ts: MidiTimeSignature): number[] => {
  const denomPow = ts.timeSignature[1] === 0 ? 2 : Math.round(Math.log2(ts.timeSignature[1]));
  return [
    ts.timeSignature[0],
    Math.max(0, Math.min(7, denomPow)),
    24, // clocks per metronome click (CC11)
    8,  // 32nd notes per beat
  ];
};

/**
 * Записывает meta-событие целиком: `FF <type> <vlq-len> <payload>`.
 */
export const writeMetaBytes = (
  out: number[],
  metaType: number,
  payload: ReadonlyArray<number>
): void => {
  out.push(0xff, metaType);
  writeVLQ(out, payload.length);
  for (const b of payload) out.push(b);
};

/**
 * Записывает текстовое meta-событие. Если `type` не входит в
 * `TEXT_META_TYPES` (см. `textTypeToByte`), возвращает `null` — caller
 * пропускает такое событие.
 */
export const writeTextMetaEvent = (m: MidiMetaEvent): number[] | null => {
  const typeByte = textTypeToByte(m.type);
  if (typeByte === null) return null;
  const list: number[] = [0xff, typeByte];
  const payload = encodeUtf8(m.text ?? "");
  writeVLQ(list, payload.length);
  for (const b of payload) list.push(b);
  return list;
};